import type { Maybe } from '@adornis/base/utilTypes.js';
import { html } from 'lit';
import type { MailProcessing } from '../../features/information-extraction/db/MailExtractionResult.js';

type PipelineStage = 'received' | 'extracting' | 'extracted' | 'reviewed' | 'exported';

const STAGES: { key: PipelineStage; label: string }[] = [
  { key: 'received', label: 'Empfangen' },
  { key: 'extracting', label: 'KI-Extraktion' },
  { key: 'extracted', label: 'Extrahiert' },
  { key: 'reviewed', label: 'Bewertet' },
  { key: 'exported', label: 'An SAP' },
];

export function getPipelineStage(mailProcessing: Maybe<MailProcessing>, isExtracting: boolean): PipelineStage {
  if (!mailProcessing) return 'received';
  if (isExtracting) return 'extracting';
  if (mailProcessing.sentToSap?.length) return 'exported';
  if (mailProcessing.evaluation != null) return 'reviewed';
  if (mailProcessing.extractionResult) return 'extracted';
  return 'received';
}

/** true when mandatory-field validation failed and the offer has not (yet) been sent to SAP */
function isBlockedFromSap(mailProcessing: Maybe<MailProcessing>, isExtracting: boolean): boolean {
  return !!(
    mailProcessing?.sapValidation &&
    !mailProcessing.sapValidation.ok &&
    !mailProcessing.sentToSap?.length &&
    !isExtracting
  );
}

/**
 * Compact, quiet pipeline: a thin row of small dots and labels rather than a dominant
 * timeline bar. Completed steps read as filled neutral (black) with a check, the current
 * step picks up the single accent, future steps stay light — so the eye is guided to where
 * work happens, not flooded with colour. The final step turns into a red "Blockiert" marker
 * when mandatory fields are missing.
 */
export function PipelineStatus(mailProcessing: Maybe<MailProcessing>, isExtracting: boolean, blocked?: boolean) {
  const currentStage = getPipelineStage(mailProcessing, isExtracting);
  const currentIndex = STAGES.findIndex(s => s.key === currentStage);
  const isBlocked = blocked ?? isBlockedFromSap(mailProcessing, isExtracting);

  return html`
    <div class="flex items-center">
      ${STAGES.map((stage, i) => {
        const isCompleted = i < currentIndex;
        const isCurrent = i === currentIndex;
        const isBlockedStep = isBlocked && stage.key === 'exported';

        // Dot: completed → filled neutral with check; current → accent; blocked → error;
        // future → hollow with a light border.
        const dotClass = isBlockedStep
          ? 'bg-error border-error text-error-content'
          : isCompleted
            ? 'bg-neutral border-neutral text-neutral-content'
            : isCurrent
              ? 'bg-primary border-primary'
              : 'bg-base-100 border-base-300';

        const labelClass = isBlockedStep
          ? 'text-error font-semibold'
          : isCurrent
            ? 'text-primary font-semibold'
            : isCompleted
              ? 'text-base-content/60 font-medium'
              : 'text-base-content/30 font-medium';

        // Connector reflects whether the step we just passed is done.
        const connectorClass = isCompleted || (isCurrent && currentIndex > 0) ? 'bg-neutral' : 'bg-base-300';

        return html`
          ${i > 0 ? html`<span class="h-px w-8 lg:w-11 mx-2 lg:mx-3 ${connectorClass}"></span>` : ''}
          <span class="flex items-center gap-1.5">
            <span
              class="flex h-4 w-4 items-center justify-center rounded-full border-[1.5px] ${dotClass}"
            >
              ${isBlockedStep
                ? html`<x-icon class="text-[11px] leading-none">priority_high</x-icon>`
                : isCompleted
                  ? html`<x-icon class="text-[11px] leading-none">check</x-icon>`
                  : ''}
            </span>
            <span class="text-[11.5px] whitespace-nowrap ${labelClass}">
              ${isBlockedStep ? 'Blockiert' : stage.label}
            </span>
          </span>
        `;
      })}
    </div>
  `;
}
